import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { setDebugSnowplowDisabled } from '@/src/analytics/debugState';

interface DebugTrackingContextValue {
  isSnowplowDisabled: boolean;
  isDebugModeActive: boolean;
  disableSnowplow: () => void;
  enableSnowplow: () => void;
  toggleSnowplow: () => void;
  setDebugModeActive: (active: boolean) => void;
}

const defaultValue: DebugTrackingContextValue = {
  isSnowplowDisabled: false,
  isDebugModeActive: false,
  disableSnowplow: () => {},
  enableSnowplow: () => {},
  toggleSnowplow: () => {},
  setDebugModeActive: () => {},
};

const DebugTrackingContext = createContext<DebugTrackingContextValue>(defaultValue);

interface DebugTrackingProviderProps {
  children: ReactNode;
}

export const DebugTrackingProvider: React.FC<DebugTrackingProviderProps> = ({ children }) => {
  const [isSnowplowDisabled, setIsSnowplowDisabled] = useState(false);
  const [isDebugModeActive, setIsDebugModeActive] = useState(false);

  const disableSnowplow = useCallback(() => {
    setIsSnowplowDisabled(true);
    setDebugSnowplowDisabled(true);
    console.log('[DEBUG TRACKING] Snowplow tracking DISABLED for this session');
  }, []);

  const enableSnowplow = useCallback(() => {
    setIsSnowplowDisabled(false);
    setDebugSnowplowDisabled(false);
    console.log('[DEBUG TRACKING] Snowplow tracking ENABLED');
  }, []);

  const toggleSnowplow = useCallback(() => {
    setIsSnowplowDisabled(prev => {
      const newValue = !prev;
      setDebugSnowplowDisabled(newValue);
      console.log(`[DEBUG TRACKING] Snowplow tracking ${newValue ? 'DISABLED' : 'ENABLED'} for this session`);
      return newValue;
    });
  }, []);

  const setDebugModeActive = useCallback((active: boolean) => {
    setIsDebugModeActive(active);
  }, []);

  return (
    <DebugTrackingContext.Provider
      value={{
        isSnowplowDisabled,
        isDebugModeActive,
        disableSnowplow,
        enableSnowplow,
        toggleSnowplow,
        setDebugModeActive,
      }}
    >
      {children}
    </DebugTrackingContext.Provider>
  );
};

export const useDebugTracking = (): DebugTrackingContextValue => {
  return useContext(DebugTrackingContext);
};

export { DebugTrackingContext };
